// Pure math for the Production Plan forecast zones (Sales Plan AI + Demand). No React, no date-fns.
// fcCal: buildCal() output for the 12 forecast months — months[{ m, segs[{ week: 'YYYY-Www', dates[] }] }];
// aiRow: the ASIN's weekly agent forecast { 'YYYY-Www': qty }; stock: opening balance of the demand chain;
// salesRow: actual daily sales { 'YYYY-MM-DD': units } (current month through yesterday); today: Miami 'YYYY-MM-DD';
// gapDays: Gap horizon per month { 'YYYY-MM': days } (missing / invalid → 90, see gapDaysOf).
// A segment = the days of one ISO week inside one month; its AI share = week qty × days / 7 (null when the
// week has no forecast — no monthly fallback). actual = Σ sales on the segment's dates < today; remaining = share − actual
// (null when share is null); elapsed = the segment has at least one date < today. Month: Σ shares / Σ remaining (null when
// no segment has one), Σ actual over segments that have a plan (= what was subtracted), any elapsed. Demand runs over all segments in calendar order: d -= remaining || 0.
// gapFc[m] = Σ remaining of the gapDays[m] days after the month (last day of m, last day of m + days]: a later segment counts
// pro rata by its dates inside the window; nulls and dates past the 12-month horizon count as 0.
// Mirrored by server/utils/production-plan-forecast.js — keep both in sync.
export const gapDaysOf = (gapDays, m) => (gapDays && gapDays[m] > 0 ? gapDays[m] : 90);
export function computeForecast(fcCal, aiRow, stock, salesRow, today, gapDays) {
  const shareBySeg = {}, remBySeg = {}, actBySeg = {}, elapsedBySeg = {}, aiByMonth = {}, remByMonth = {}, actByMonth = {}, elapsedByMonth = {}, dmBySeg = {}, dmByMonth = {}, gapFcByMonth = {}, gapDaysByMonth = {};
  let d = Number(stock) || 0;
  for (const { m, segs } of fcCal.months) {
    let sum = null, rem = null, act = 0, elapsed = false;
    for (const s of segs) {
      const k = `${m}|${s.week}`, v = aiRow && aiRow[s.week];
      const share = v == null ? null : (Number(v) || 0) * s.dates.length / 7;
      let a = 0, e = false;
      if (today) for (const dt of s.dates) if (dt < today) { e = true; a += Number(salesRow && salesRow[dt]) || 0; }
      const r = share == null ? null : share - a;
      shareBySeg[k] = share; remBySeg[k] = r; actBySeg[k] = a; elapsedBySeg[k] = e;
      if (share != null) { sum = (sum || 0) + share; rem = (rem || 0) + r; }
      if (share != null) act += a;   // month actual = only what was subtracted (segments that have a plan)
      elapsed = elapsed || e;
      d -= r || 0;
      dmBySeg[k] = d;
    }
    aiByMonth[m] = sum; remByMonth[m] = rem; actByMonth[m] = act; elapsedByMonth[m] = elapsed;
    dmByMonth[m] = d;
  }
  const ms = fcCal.months.map(x => x.m);
  ms.forEach((m, i) => {
    const days = gapDaysOf(gapDays, m);
    const cut = new Date(Date.UTC(+m.slice(0, 4), +m.slice(5, 7), 0) + days * 864e5).toISOString().slice(0, 10);   // last day of m + days
    let sum = 0;
    for (let j = i + 1; j < ms.length && ms[j] <= cut.slice(0, 7); j++)
      for (const s of fcCal.months[j].segs) { const r = remBySeg[`${ms[j]}|${s.week}`]; if (r) sum += r * s.dates.filter(dt => dt <= cut).length / s.dates.length; }
    gapFcByMonth[m] = sum; gapDaysByMonth[m] = days;
  });
  return { shareBySeg, remBySeg, actBySeg, elapsedBySeg, aiByMonth, remByMonth, actByMonth, elapsedByMonth, dmBySeg, dmByMonth, gapFcByMonth, gapDaysByMonth };
}

// Production Gap of a month over its horizon (days = gapDaysByMonth), negative = not enough: noProd = Demand − gapFc (if nothing
// is produced), plan = Demand Plan (cumPlan + Demand) − gapFc (with the current plan). null when the month has no forecast.
export function gapValues(fc, cumPlan, m) {
  const dm = fc.dmByMonth[m];
  if (dm == null) return null;
  return { noProd: dm - fc.gapFcByMonth[m], plan: (cumPlan[m] || 0) + dm - fc.gapFcByMonth[m], days: fc.gapDaysByMonth[m] };
}
// Sort key of one product for view.sort { kind, m }: 'dm' = the Demand month cell (dmByMonth, or the Demand Plan
// cumPlan + dm in Planner View), 'gap' = Production Gap with the current plan (gapValues().plan; asc = biggest
// shortages first). null when the month has no forecast.
export function sortValue(fc, cumPlan, sort, plannerView) {
  const dm = fc.dmByMonth[sort.m];
  if (dm == null) return null;
  return sort.kind === 'gap' ? gapValues(fc, cumPlan, sort.m).plan : plannerView ? (cumPlan[sort.m] || 0) + dm : dm;
}
// Numeric comparator; null/undefined sort last in both directions.
export const cmpNullsLast = dir => (a, b) => (a == null || b == null ? (a == null) - (b == null) : dir === 'desc' ? b - a : a - b);
